import { Platform, View } from 'react-native'
import React, { useEffect } from 'react'
import { scheduleOnRN, scheduleOnUI } from 'react-native-worklets'
import Animated, {
  FadeInUp,
  FadeOutUp,
  measure,
  useAnimatedRef,
  useAnimatedStyle,
  useSharedValue,
  withSpring,
} from 'react-native-reanimated'
import { Portal } from '@gorhom/portal'
import FullWindowOverlay from '@ui/compat/FullWindowOverlay'
import cn from '@ui/utils/cn'
import { Gesture, GestureDetector } from 'react-native-gesture-handler'
import { PopoverProps } from './types'
import { useMaxWindowDimensions } from '@ui/theme/useRWD'

export default function Popover({
  children,
  open,
  onClose,
  content,
  offset = [0, 0],
  position = ['bottom', 'right'],
  className,
  customWidth,
}: PopoverProps) {
  const { height: windowH } = useMaxWindowDimensions()

  const active = useSharedValue(0)
  const animatedRef = useAnimatedRef<Animated.View>()
  const [visible, setVisible] = React.useState(false)
  const pageY = useSharedValue(0)
  const pageX = useSharedValue(0)
  const [yPos, xPos] = position
  const height = useSharedValue(0)

  const width = useSharedValue(0)

  useEffect(() => {
    active.set(withSpring(open ? 1 : 0))
    if (!open) {
      setVisible(false)
      return
    }

    scheduleOnUI(() => {
      const measurement = measure(animatedRef)
      if (measurement === null) {
        return
      }
      pageY.value = measurement.pageY
      pageX.value = measurement.pageX
      width.value = measurement.width
      height.value = measurement.height
      scheduleOnRN(setVisible, true)
    })
  }, [open])

  const style = useAnimatedStyle(() => {
    const isTop = yPos === 'top'
    const isLeft = xPos === 'left'
    const oX = typeof offset === 'number' ? 0 : offset[0]
    const oY = typeof offset === 'number' ? offset : offset[1]

    // const yDir = isTop ? -1 : 1
    const yOffset = height.value + oY
    const widthOffset =
      typeof customWidth === 'number'
        ? customWidth
        : customWidth?.value || width.value
    const top = !isTop ? pageY.value + yOffset /** yDir*/ : undefined
    // 'left': left edge of dropdown aligns with left edge of trigger
    // 'right': right edge of dropdown aligns with right edge of trigger
    const leftRaw = isLeft
      ? pageX.value + oX
      : pageX.value - widthOffset + width.value + oX
    const left = leftRaw
    const bottom = isTop ? windowH - pageY.value - oY : undefined
    const styleObj = {
      top,
      left,
      width: widthOffset,
      bottom,
    }
    return styleObj
  })

  return (
    <Animated.View ref={animatedRef} className={cn('relative', className)}>
      {children}

      {open && (
        <Portal>
          <FullWindowOverlay
            // @ts-expect-error - this for web and android
            className={cn('h-full w-full flex-1', {
              'pointer-events-none': !open,
              'fixed inset-0 h-screen w-screen': Platform.OS === 'web',
            })}
          >
            {visible && (
              <>
                <GestureDetector
                  gesture={Gesture.Tap().onStart(() => scheduleOnRN(onClose))}
                >
                  <View style={{ flex: 1 }} />
                </GestureDetector>
                <Animated.View
                  style={[style]}
                  entering={FadeInUp.duration(200)}
                  exiting={FadeOutUp.duration(200).withCallback(() =>
                    scheduleOnRN(onClose)
                  )}
                  className="absolute"
                >
                  {content}
                </Animated.View>
              </>
            )}
          </FullWindowOverlay>
        </Portal>
      )}
    </Animated.View>
  )
}
